import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Store, Play } from 'lucide-react';
import '../app.css';

const tutorialCategories = ['All', 'Customers', 'Merchants'];

const tutorialsData = [
    {
        title: 'Getting Started with Digital Wallet',
        category: 'Customers',
        duration: '5 min',
        thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
    {
        title: 'How to Make QR Code Payments',
        category: 'Customers',
        duration: '3 min',
        thumbnail: 'https://images.unsplash.com/photo-1622012864015-a832e1b3a3c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
    {
        title: 'Setting Up Your Merchant Account',
        category: 'Merchants',
        duration: '8 min',
        thumbnail: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
    {
        title: 'Integrating POS System with Your Store',
        category: 'Merchants',
        duration: '12 min',
        thumbnail: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
    {
        title: 'Managing Transaction History',
        category: 'Customers',
        duration: '4 min',
        thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
    {
        title: 'Analyzing Sales Reports',
        category: 'Merchants',
        duration: '10 min',
        thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
    },
];

const Tutorials = () => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredTutorials, setFilteredTutorials] = useState(tutorialsData);
    const tutorialRefs = useRef([]);

    useEffect(() => {
        const filtered = tutorialsData.filter((tutorial) => {
            const matchesCategory = activeCategory === 'All' || tutorial.category === activeCategory;
            const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
        setFilteredTutorials(filtered);
    }, [activeCategory, searchTerm]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-fade-in');
                        entry.target.classList.remove('opacity-0');
                        entry.target.classList.remove('translate-y-10');
                    } else {
                        entry.target.classList.remove('animate-fade-in');
                        entry.target.classList.add('opacity-0');
                        entry.target.classList.add('translate-y-10');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const currentRefs = tutorialRefs.current;
        currentRefs.forEach((tutorial) => {
            if (tutorial) observer.observe(tutorial);
        });

        return () => {
            currentRefs.forEach((tutorial) => {
                if (tutorial) observer.unobserve(tutorial);
            });
        };
    }, [filteredTutorials]);

    return (
        <section id="tutorials" className="py-20 min-h-screen bg-gradient-to-b from-[#0a1e3a] to-[#193763]">
            <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
                    Tutorials
                </h2>
                <p className="text-center text-gray-300 mb-8 max-w-2xl mx-auto">
                    Learn how to make the most of HISABB with our comprehensive guides and video tutorials
                </p>

                <div className="max-w-2xl mx-auto mb-8">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search tutorials..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-3 pl-12 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3370C9] focus:border-transparent"
                        />
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    </div>
                </div>

                <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-white rounded-lg p-1 shadow-md">
                        {tutorialCategories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-6 py-2 rounded-md transition-all duration-300 ${
                                    activeCategory === category
                                        ? 'bg-[#3370C9] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {category === 'Customers' ? (
                                    <span className="flex items-center">
                                        <User size={16} className="mr-2" />
                                        {category}
                                    </span>
                                ) : category === 'Merchants' ? (
                                    <span className="flex items-center">
                                        <Store size={16} className="mr-2" />
                                        {category}
                                    </span>
                                ) : (
                                    category
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredTutorials.map((tutorial, index) => (
                        <div
                            key={index}
                            ref={(el) => (tutorialRefs.current[index] = el)}
                            className="bg-white rounded-lg shadow-md overflow-hidden opacity-0 translate-y-10 transition-all duration-500"
                            style={{ transitionDelay: `${index * 100}ms` }}
                        >
                            <div className="relative group">
                                <img
                                    src={tutorial.thumbnail}
                                    alt={tutorial.title}
                                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-[#193763]/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <button className="bg-white/90 text-[#193763] p-3 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300">
                                        <Play size={24} />
                                    </button>
                                </div>
                                <span className="absolute bottom-2 right-2 bg-[#193763] text-white px-2 py-1 text-xs rounded">
                                    {tutorial.duration}
                                </span>
                            </div>
                            <div className="p-4">
                                <span className="inline-block px-3 py-1 bg-[#3370C9]/10 text-[#3370C9] text-xs rounded-full mb-2">
                                    {tutorial.category}
                                </span>
                                <h3 className="text-lg font-semibold text-[#193763] mb-2">{tutorial.title}</h3>
                                <button className="text-[#3370C9] text-sm font-medium hover:underline">
                                    Watch Tutorial
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Tutorials;